import { useEffect, useMemo } from "react";
import { pubsub } from "utils";
import { useMemberUser } from "./useMemberUser";
import { pubSubKeyMap } from "@/src/utils/pubSubKeyMap";
import { sleep } from "@/src/utils/sleep";

// 模块级单例订阅：无论有多少个 useCredits 实例同时存在，
// pubsub 事件只注册一个监听器，只触发一次 refetch，避免重复请求。
let _instanceCount = 0;
let _pubsubOff: (() => void) | null = null;
const _refetchRef = { current: null as (() => void) | null };

const REFETCH_EVENTS = [
  pubSubKeyMap.TASK_COMPLETED_CREDITS,
  pubSubKeyMap.MEMBER_PURCHASE_SUCCESS,
  pubSubKeyMap.AI_ANSWER_COMPLETED,
  pubSubKeyMap.USER_VIEW_DAILY_SUMMARY,
];

function ensureSubscribed() {
  if (_pubsubOff) {
    return;
  }
  _pubsubOff = pubsub.on(REFETCH_EVENTS, async () => {
    await sleep(1000);
    _refetchRef.current?.();
  });
}

function releaseSubscription() {
  if (_instanceCount > 0) {
    return;
  }
  _pubsubOff?.();
  _pubsubOff = null;
}

/**
 * 获取用户credits数量的Hook
 * 支持通过pubsub事件自动刷新credits
 */
export const useCredits = () => {
  const { data: memberUserData, refetch, ...rest } = useMemberUser();

  const credits = useMemo(() => {
    return memberUserData?.credit?.available ?? 0;
  }, [memberUserData]);

  useEffect(() => {
    _instanceCount++;
    // 始终保持指向最新的 refetch，确保 query key 变化后仍然有效
    _refetchRef.current = refetch;
    ensureSubscribed();
    return () => {
      _instanceCount--;
      releaseSubscription();
    };
  }, [refetch]);

  return {
    credits,
    isYearMember: memberUserData?.isYearMember,
    memberUserData,
    ...rest,
  };
};
